// Match outcome reporting. Replaces the brittle proposal/confirm WS
// dance. Each client independently POSTs its outcome to the server,
// which arbitrates and pushes a `match:settled` WS event with the
// per-player reward envelope.
//
// Two failure modes handled here:
//   1. Transient network blip mid-POST → retry with backoff (3
//      attempts; each one is idempotent server-side).
//   2. App closed before any retry can land → enqueue in localStorage
//      via pendingReports.js and drain on next launch.

import { api } from '../serverClient';
import { savePendingReport, removePendingReport } from './pendingReports';

const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [500, 2000, 8000];

function isNetworkLevelError(err) {
  // `TypeError: Failed to fetch` (Chromium) / `Load failed` (WebKit) /
  // `NetworkError` (Firefox) all bubble up as bare TypeErrors. HTTP
  // errors come back as `new Error('<status> <statusText>')` from
  // serverClient and carry a numeric prefix.
  if (!err) return false;
  if (err.name === 'TypeError') return true;
  const msg = String(err.message || '');
  if (/^\d{3}\s/.test(msg)) return false;
  return /failed to fetch|load failed|network/i.test(msg);
}

async function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Submit the local player's match outcome.
 *
 * @param {object} params
 * @param {string} params.roomCode
 * @param {boolean} params.won
 * @param {string} [params.reason='engine_resolved']
 * @param {string} [params.deckElement]
 * @param {string[]} [params.deckElements]
 * @param {string} [params.avatarId]
 *
 * @returns {Promise<{ status: 'settled'|'cached'|'pending', envelope: object|null, roomCode: string }>}
 *
 * `settled` / `cached`: the server has the reward envelope in hand
 * and the caller can render rewards immediately. `pending`: the
 * server accepted the report but is waiting on the opponent's; the
 * caller should listen for the `match:settled` WS push to receive
 * the envelope.
 *
 * On exhausted network retries, persists the report to the outbox
 * and re-throws. The caller is expected to surface the error to
 * the user; the next app launch will drain the outbox automatically.
 */
export async function reportMatchOutcome({
  roomCode,
  won,
  reason = 'engine_resolved',
  deckElement = null,
  deckElements = [],
  avatarId = null,
}) {
  if (!roomCode || typeof roomCode !== 'string') {
    throw new Error('reportMatchOutcome: roomCode is required');
  }

  const payload = {
    won: !!won,
    reason,
    deckElement,
    deckElements,
    avatarId,
  };

  let lastErr = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const result = await api.post(`/matches/${encodeURIComponent(roomCode)}/report`, payload);
      // Success: clear any stale outbox entry for this room (drained
      // from a previous session, succeeded this session, etc.).
      removePendingReport(roomCode);
      return result;
    } catch (err) {
      lastErr = err;
      // Server-side 4xx/5xx. Don't retry network-style, but the
      // server told us something useful. 404 with no_room means the
      // match window has fully expired; no point retrying.
      if (!isNetworkLevelError(err)) {
        const msg = String(err.message || '');
        const is404 = /^404\b/.test(msg) || /no active or recent match/i.test(msg);
        if (is404) {
          removePendingReport(roomCode);
        }
        throw err;
      }
      // Network blip. Persist before sleeping so a hard app close
      // mid-backoff still leaves a trail to drain on next launch.
      savePendingReport({ roomCode, payload });
      if (attempt < MAX_ATTEMPTS - 1) {
        await delay(BACKOFF_MS[attempt]);
      }
    }
  }
  // All retries exhausted on network errors. Leave the outbox entry
  // in place so the next app launch (or a future drain) picks it up.
  throw lastErr;
}
